import { useNostr } from '@nostrify/react';
import { useQuery } from '@tanstack/react-query';
import type { NostrEvent } from '@nostrify/nostrify';

import { ORGANIZER_PUBKEY, POLL_RELAYS, POLL_KIND } from '@/lib/constants';

export interface PollOption {
  index: string;
  label: string;
}

export interface ZapPoll {
  event: NostrEvent;
  question: string;
  options: PollOption[];
  valueMinimum: number;
  valueMaximum: number;
  closedAt?: number;
}

function parseZapPoll(event: NostrEvent): ZapPoll | null {
  if (event.kind !== POLL_KIND) return null;

  const options: PollOption[] = [];
  let valueMinimum = 1;
  let valueMaximum = 21000;
  let closedAt: number | undefined;

  for (const tag of event.tags) {
    if (tag[0] === 'poll_option' && tag[1] !== undefined && tag[2]) {
      options.push({ index: tag[1], label: tag[2] });
    } else if (tag[0] === 'value_minimum' && tag[1]) {
      valueMinimum = parseInt(tag[1], 10);
    } else if (tag[0] === 'value_maximum' && tag[1]) {
      valueMaximum = parseInt(tag[1], 10);
    } else if (tag[0] === 'closed_at' && tag[1]) {
      closedAt = parseInt(tag[1], 10);
    }
  }

  if (options.length < 2) return null;

  return {
    event,
    question: event.content,
    options,
    valueMinimum,
    valueMaximum,
    closedAt,
  };
}

/**
 * Fetch all kind 6969 zap polls from the organizer that are
 * replies to a specific round's announcement thread.
 *
 * @param roundEventId - The event ID of the round's announcement note
 */
export function usePolls(roundEventId: string) {
  const { nostr } = useNostr();

  return useQuery({
    queryKey: ['maxi-madness', 'polls', roundEventId],
    queryFn: async () => {
      const allEvents: NostrEvent[] = [];

      // Query across all configured relays
      const relayGroup = nostr.group(POLL_RELAYS);

      try {
        const events = await relayGroup.query(
          [
            {
              kinds: [POLL_KIND],
              authors: [ORGANIZER_PUBKEY],
              '#e': [roundEventId],
              limit: 200,
            },
          ],
          { signal: AbortSignal.timeout(12000) },
        );
        allEvents.push(...events);
      } catch {
        // Relay group query failed
      }

      // Also query the default pool as a fallback
      try {
        const poolEvents = await nostr.query(
          [
            {
              kinds: [POLL_KIND],
              authors: [ORGANIZER_PUBKEY],
              '#e': [roundEventId],
              limit: 200,
            },
          ],
          { signal: AbortSignal.timeout(10000) },
        );
        allEvents.push(...poolEvents);
      } catch {
        // Pool query failed
      }

      // Some polls may be replies to other polls in the thread (chained).
      const firstPassIds = new Set(allEvents.map(e => e.id));

      if (firstPassIds.size > 0) {
        try {
          const chainedEvents = await relayGroup.query(
            [{
              kinds: [POLL_KIND],
              authors: [ORGANIZER_PUBKEY],
              '#e': [...firstPassIds],
              limit: 200,
            }],
            { signal: AbortSignal.timeout(8000) },
          );
          allEvents.push(...chainedEvents);
        } catch {
          // Chained query failed
        }
      }

      // Deduplicate
      const seen = new Set<string>();
      const uniqueEvents: NostrEvent[] = [];
      for (const event of allEvents) {
        if (!seen.has(event.id)) {
          seen.add(event.id);
          uniqueEvents.push(event);
        }
      }

      // Parse into ZapPolls
      const polls: ZapPoll[] = [];
      for (const event of uniqueEvents) {
        const poll = parseZapPoll(event);
        if (poll) {
          polls.push(poll);
        }
      }

      // Sort by created_at ascending (oldest first)
      polls.sort((a, b) => a.event.created_at - b.event.created_at);

      return polls;
    },
    staleTime: 30_000,
    retry: 3,
  });
}
